import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { message, Row, Col, Tabs } from 'antd'
import { PageHeader } from '../../Common'
import { toPath } from '../../../routes'

import usePracticeCycles from '../../Common/usePracticeCycles'

import { useIsMobile } from '../../../hooks'
import { fileToDataUrl } from '../../../lib/file'
import PDFPreview from '../../Common/PDFPreview'

import PatientForm from './PatientForm'
import CreateTransmission from './UseCase/Results'
import ReportUpload from './ReportUpload'

const { TabPane } = Tabs

export default function Upload() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [patient, setPatient] = useState()
  const [pdfUrl, setPdfUrl] = useState()
  const [file, setFile] = useState()
  const { practiceId } = useParams()
  // eslint-disable-next-line no-unused-vars
  const [_, { practice }] = usePracticeCycles()

  const onSubmit = useCallback(
    async (payload, { setErrors }) => {
      const {
        patientId,
        firstName,
        lastName,
        dateOfBirth,
        practiceMrn,
        deviceModelId,
        deviceSerialNumber,
        dateOfCheck,
        transmissionDate,
      } = payload
      const showErrors = errors =>
        typeof errors === 'string' ? message.error(errors) : setErrors(errors)

      try {
        const [patientCreationResult, createUploadResult] =
          await CreateTransmission(
            patient?.id || patientId,
            practiceId,
            patient,
            {
              firstName,
              lastName,
              dateOfBirth,
              practiceMrn,
              deviceModelId,
              deviceSerialNumber,
              dateOfCheck,
              transmissionDate,
            },
            file,
          )
        if (
          patientCreationResult.isSuccess() &&
          createUploadResult.isSuccess()
        ) {
          setPatient(undefined)

          message.success('Transmission successfully created')
          navigate(toPath.transmissions())

          return
        }

        if (!createUploadResult.isSuccess()) {
          message.error(createUploadResult.getErrorMessage())
          return
        }

        if (!patientCreationResult.isSuccess()) {
          showErrors(patientCreationResult.getErrors())
        }
      } catch (e) {
        message.error(`Something went wrong. ${e.message}`)
        throw e
      }
    },
    [patient, practiceId, file, navigate],
  )

  const onReportUpload = async payload => {
    const pdf = payload.find(blob => blob.name.toLowerCase().endsWith('.pdf'))

    if (!pdf) {
      message.error('Please upload a PDF file')
      return
    }

    let previewUrl = null
    if (pdf) previewUrl = await fileToDataUrl(pdf)
    setFile(pdf)
    setPdfUrl(previewUrl)
  }

  const reportPane = (
    <>
      {!pdfUrl && <ReportUpload onUpload={onReportUpload} />}
      {pdfUrl && <PDFPreview url={pdfUrl} perPage={1} hidePagination />}
    </>
  )

  const formPane = <PatientForm practiceId={practiceId} onSubmit={onSubmit} />

  return (
    <div>
      <PageHeader title="Add Transmission" subTitle={practice?.name} />
      {isMobile ? (
        // Half a phone is not enough for either side: the drop zone loses its
        // hit area and the form loses its inputs. Same two panes, one at a time.
        <Tabs defaultActiveKey="report">
          <TabPane tab="Report" key="report">
            {reportPane}
          </TabPane>
          <TabPane tab="Patient" key="patient">
            {formPane}
          </TabPane>
        </Tabs>
      ) : (
        <Row gutter={{ sm: 12 }}>
          <Col span={12}>{reportPane}</Col>
          <Col span={12}>{formPane}</Col>
        </Row>
      )}
    </div>
  )
}
